/**
 * OMNI Wishlist Core - 独立した堅牢なwishlist実装
 * - import/require不使用（クラシックJS）
 * - 他のJSエラーに影響されない
 * - セレクタ例外を安全に処理
 */

// ===== グローバル名前空間の確保（トップレベル・即時実行） =====
window.OMNI = window.OMNI || {};
window.OMNI.wishlist = window.OMNI.wishlist || {};
window.OMNI.wishlist.debug = window.OMNI.wishlist.debug || {};

(() => {
  "use strict";

  // ===== 定数 =====
  const ANON_ID_KEY = "omni_anon_id";
  const WISHLIST_ITEMS_KEY = "omni_wishlist_items"; // Wishlist専用キー
  const RECENTLY_VIEWED_KEY = "omni_recently_viewed";
  const GUEST_TTL_DAYS = 60;
  const GUEST_TTL_MS = GUEST_TTL_DAYS * 24 * 60 * 60 * 1000;
  const TTL_MS = 60 * 24 * 60 * 60 * 1000; // 60日

  // ===== 安全なセレクタユーティリティ =====
  const sanitizeSelector = (s) => String(s || "").replace(/[\u0000-\u001F\u007F]/g, "");

  const escapeSelector = (s) => {
    const str = sanitizeSelector(s);
    if (window.CSS && CSS.escape) {
      try {
        return CSS.escape(str);
      } catch (e) {
        console.warn("[wishlist] CSS.escape failed", e);
      }
    }
    // フォールバック: 基本的なエスケープ
    return str.replace(/["\\]/g, "\\$&");
  };

  const safeQS = (sel, root) => {
    root = root || document;
    try {
      const sanitized = sanitizeSelector(sel);
      return root.querySelector(sanitized);
    } catch (e) {
      console.warn("[wishlist] bad selector", sel, e);
      return null;
    }
  };

  const safeQSA = (sel, root) => {
    root = root || document;
    try {
      const sanitized = sanitizeSelector(sel);
      return Array.from(root.querySelectorAll(sanitized));
    } catch (e) {
      console.warn("[wishlist] bad selector", sel, e);
      return [];
    }
  };

  // ===== ユーティリティ関数 =====
  function getAnonId() {
    var anon = null;
    try {
      anon = localStorage.getItem(ANON_ID_KEY);
    } catch (e) {
      console.warn("[wishlist] localStorage read error", e);
    }
    if (!anon) {
      anon = crypto.randomUUID ? crypto.randomUUID() : "anon-" + Date.now() + "-" + Math.random().toString(36).slice(2);
      try {
        localStorage.setItem(ANON_ID_KEY, anon);
      } catch (e) {
        console.warn("[wishlist] localStorage write error", e);
      }
    }
    return anon;
  }

  function isLoggedIn() {
    return !!(window.OMNI && window.OMNI.isLoggedIn === true);
  }

  function getCustomerId() {
    return (window.OMNI && window.OMNI.customerId) ? String(window.OMNI.customerId) : "";
  }

  // ===== LocalStorage操作（統一実装） =====
  
  function _safeParse(json, fallback) {
    try {
      return JSON.parse(json);
    } catch (e) {
      return fallback;
    }
  }

  /**
   * LocalStorageからwishlistアイテムを取得
   * 形式: { items: [{handle, productGid, addedAt}] }
   */
  function getLocalWishlistItems() {
    try {
      var raw = localStorage.getItem(WISHLIST_ITEMS_KEY);
      var obj = _safeParse(raw || "", { items: [] });
      return Array.isArray(obj.items) ? obj.items : [];
    } catch (e) {
      console.warn("[wishlist] getLocalWishlistItems error", e);
      return [];
    }
  }

  /**
   * LocalStorageにwishlistアイテムを保存
   */
  function saveLocalWishlistItems(items) {
    try {
      localStorage.setItem(WISHLIST_ITEMS_KEY, JSON.stringify({ items: items }));
    } catch (e) {
      console.warn("[wishlist] saveLocalWishlistItems error", e);
    }
  }

  /**
   * 60日TTLでクリーンアップ
   */
  function cleanupLocalWishlistItems() {
    var now = Date.now();
    var items = getLocalWishlistItems();
    var before = items.length;
    items = items.filter(function(it) {
      var at = Number(it && it.addedAt ? it.addedAt : 0);
      return at && (now - at) <= TTL_MS;
    });
    if (items.length !== before) {
      console.log("[wishlist] cleanupLocalWishlistItems: removed " + (before - items.length) + " expired items");
      saveLocalWishlistItems(items);
    }
    return items;
  }

  function removeLocalWishlistItem(handle) {
    var items = getLocalWishlistItems();
    var filtered = items.filter(function(item) { return item.handle !== handle; });
    saveLocalWishlistItems(filtered);
    return filtered;
  }

  function getRecentlyViewed() {
    try {
      var data = localStorage.getItem(RECENTLY_VIEWED_KEY);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      return [];
    }
  }

  // ===== API関数 =====
  
  /**
   * getHandles() - wishlistアイテムのhandle配列を取得
   * 
   * 仕様:
   * - ゲスト（未ログイン）: LocalStorageから取得（サーバーには行かない）
   * - ログイン時: GET /apps/wishlist/list を叩く
   * 
   * @returns {Promise<string[]>} handle配列
   */
  function getHandles() {
    var loggedIn = isLoggedIn();
    console.log("[wishlist] getHandles: isLoggedIn=" + loggedIn);

    if (!loggedIn) {
      // ゲスト: LocalStorageから取得（サーバーには行かない）
      var items = cleanupLocalWishlistItems();
      // addedAt降順でソート
      items.sort(function(a, b) {
        return (b.addedAt || 0) - (a.addedAt || 0);
      });
      var handles = items.map(function(it) { return it.handle; }).filter(Boolean);
      console.log("[wishlist] getHandles: guest mode, handles.length=" + handles.length);
      return Promise.resolve(handles);
    }

    // ログイン時: サーバーから取得
    return fetch("/apps/wishlist/list", {
      credentials: "same-origin",
      headers: { "Accept": "application/json" }
    })
    .then(function(res) {
      console.log("[wishlist] getHandles: /apps/wishlist/list status=" + res.status);
      if (!res.ok) {
        console.warn("[wishlist] getHandles: server returned " + res.status + ", returning empty array");
        return [];
      }
      return res.json();
    })
    .then(function(data) {
      if (data && data.ok && Array.isArray(data.handles)) {
        console.log("[wishlist] getHandles: server returned handles.length=" + data.handles.length);
        return data.handles;
      }
      console.log("[wishlist] getHandles: server returned invalid data, returning empty array");
      return [];
    })
    .catch(function(err) {
      console.warn("[wishlist] getHandles: fetch error, returning empty array", err);
      return [];
    });
  }

  /**
   * getCount() - wishlistアイテムの件数を取得
   * 
   * @returns {Promise<number>} 件数
   */
  function getCount() {
    return getHandles().then(function(handles) {
      return handles.length;
    }).catch(function(err) {
      console.warn("[wishlist] getCount error", err);
      return 0;
    });
  }

  // 後方互換のため残す（非推奨）
  function fetchWishlistFromServer() {
    console.warn("[wishlist] fetchWishlistFromServer is deprecated, use getHandles() instead");
    return getHandles().then(function(handles) {
      // 旧形式に変換
      return handles.map(function(handle) {
        return {
          handle: handle,
          productGid: null,
          addedAt: Date.now()
        };
      });
    }).catch(function(err) {
      console.warn("[wishlist] fetchWishlistFromServer error", err);
      return null;
    });
  }

  function removeFromServer(productGid) {
    var anonId = getAnonId();
    return fetch("/apps/wishlist/toggle", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      body: JSON.stringify({ product_gid: productGid, anon_id: anonId })
    })
    .then(function(res) { return res.json(); })
    .then(function(data) {
      return data.ok && data.active === false;
    })
    .catch(function(err) {
      console.warn("[wishlist] removeFromServer error", err);
      return false;
    });
  }

  function fetchProductCard(handle) {
    if (!handle) return Promise.resolve(null);
    var safeHandle = escapeSelector(handle);
    return fetch("/products/" + encodeURIComponent(handle) + "?view=omni-wishlist-card")
    .then(function(res) {
      if (!res.ok) return null;
      return res.text();
    })
    .then(function(html) {
      return html ? html.trim() : null;
    })
    .catch(function(err) {
      console.warn("[wishlist] fetchProductCard error for " + handle, err);
      return null;
    });
  }

  // ===== DOM要素キャッシュ =====
  var elements = {};

  function cacheElements() {
    elements.grid = safeQS("[data-wishlist-grid]");
    elements.loading = safeQS("[data-wishlist-loading]");
    elements.empty = safeQS("[data-wishlist-empty]");
    elements.controls = safeQS("[data-wishlist-controls]");
    elements.pageCount = safeQS("[data-wishlist-count]");
    elements.filterCategory = safeQS('[data-wishlist-filter="category"]');
    elements.filterRank = safeQS('[data-wishlist-filter="rank"]');
    elements.filterInStock = safeQS('[data-wishlist-filter="instock"]');
    elements.sortSelect = safeQS("[data-wishlist-sort]");
    elements.recentlyViewedGrid = safeQS("[data-wishlist-recently-viewed-grid]");
    elements.recentlyViewedSection = safeQS("[data-wishlist-recently-viewed]");
    elements.recommendationsGrid = safeQS("[data-wishlist-recommendations-grid]");
    elements.recommendationsSection = safeQS("[data-wishlist-recommendations]");
  }

  // ===== 状態管理 =====
  var wishlistItems = [];
  var loadedCards = {};
  var categories = {};
  var ranks = {};

  // ===== 描画関数 =====
  function showLoading(show) {
    if (elements.loading) {
      elements.loading.style.display = show ? "flex" : "none";
    }
  }

  function showEmpty(show) {
    if (elements.empty) {
      elements.empty.style.display = show ? "block" : "none";
    }
    if (elements.grid) {
      elements.grid.style.display = show ? "none" : "grid";
    }
    if (elements.controls) {
      elements.controls.style.display = show ? "none" : "flex";
    }
  }

  function updatePageCount(count) {
    if (elements.pageCount) {
      elements.pageCount.textContent = String(count);
    }
    // ヘッダーバッジ更新イベント
    try {
      window.dispatchEvent(new CustomEvent("omni:wishlist:count", { detail: { count: count } }));
    } catch (e) {
      console.warn("[wishlist] dispatchEvent error", e);
    }
  }

  /**
   * Wishlistカウント変更イベントを発火（統一インターフェース）
   * @param {number} count - 新しい件数
   * @param {object} context - コンテキスト情報（reason, handle等）
   */
  function dispatchCountChangeEvent(count, context) {
    context = context || {};
    try {
      window.dispatchEvent(new CustomEvent("omni:wishlist:count-change", {
        detail: { count: count, reason: context.reason, handle: context.handle, productId: context.productId, productGid: context.productGid }
      }));
      // 互換性のため、既存イベントも発火
      window.dispatchEvent(new CustomEvent("omni:wishlist:count", { detail: { count: count } }));
    } catch (e) {
      console.warn("[wishlist] dispatchCountChangeEvent error", e);
    }
  }

  /**
   * カードHTMLからカード要素を抽出
   * ★ querySelector('.omni-wishlist-card-wrapper') で確実に抽出
   */
  function renderCard(html, item) {
    var wrap = document.createElement("div");
    wrap.innerHTML = html;
    
    // ★ラッパー要素で抜く（商品一覧と同じcard-productを含む）
    var card = wrap.querySelector(".omni-wishlist-card-wrapper");
    
    if (!card) {
      console.warn("[wishlist] card node not found in response", html.slice(0, 200));
      return null;
    }
    
    if (card && item && item.addedAt) {
      card.setAttribute("data-added-at", String(item.addedAt));
    }
    return card;
  }

  function populateCategoryFilter() {
    if (!elements.filterCategory) return;
    var currentValue = elements.filterCategory.value;
    elements.filterCategory.innerHTML = '<option value="">すべて</option>';
    var cats = Object.keys(categories).sort();
    cats.forEach(function(cat) {
      var option = document.createElement("option");
      option.value = cat;
      option.textContent = cat;
      elements.filterCategory.appendChild(option);
    });
    if (categories[currentValue]) {
      elements.filterCategory.value = currentValue;
    }
  }

  function populateRankFilter() {
    if (!elements.filterRank) return;
    var currentValue = elements.filterRank.value;
    elements.filterRank.innerHTML = '<option value="">すべて</option>';
    var rankOrder = ["N", "N-", "S", "A", "B", "C", "D", "E"];
    var rankList = Object.keys(ranks).sort(function(a, b) {
      var indexA = rankOrder.indexOf(a);
      var indexB = rankOrder.indexOf(b);
      if (indexA === -1 && indexB === -1) return a.localeCompare(b);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
    rankList.forEach(function(rank) {
      var option = document.createElement("option");
      option.value = rank;
      option.textContent = rank;
      elements.filterRank.appendChild(option);
    });
    if (ranks[currentValue]) {
      elements.filterRank.value = currentValue;
    }
  }

  function applyFilterAndSort() {
    if (!elements.grid) return;

    var filterCategory = elements.filterCategory ? elements.filterCategory.value : "";
    var filterRank = elements.filterRank ? elements.filterRank.value : "";
    var filterInStock = elements.filterInStock ? elements.filterInStock.checked : true;
    var sortBy = elements.sortSelect ? elements.sortSelect.value : "added-desc";

    var cards = [];
    for (var handle in loadedCards) {
      if (loadedCards.hasOwnProperty(handle)) {
        cards.push(loadedCards[handle]);
      }
    }

    // フィルタ
    cards = cards.filter(function(card) {
      if (filterCategory && card.getAttribute("data-category") !== filterCategory) return false;
      if (filterRank && card.getAttribute("data-rank") !== filterRank) return false;
      if (filterInStock && card.getAttribute("data-in-stock") === "false") return false;
      return true;
    });

    // ソート
    cards.sort(function(a, b) {
      var aVal, bVal;
      switch (sortBy) {
        case "added-desc":
          aVal = parseInt(a.getAttribute("data-added-at") || "0", 10);
          bVal = parseInt(b.getAttribute("data-added-at") || "0", 10);
          return bVal - aVal;
        case "added-asc":
          aVal = parseInt(a.getAttribute("data-added-at") || "0", 10);
          bVal = parseInt(b.getAttribute("data-added-at") || "0", 10);
          return aVal - bVal;
        case "price-asc":
          aVal = parseInt(a.getAttribute("data-price") || "0", 10);
          bVal = parseInt(b.getAttribute("data-price") || "0", 10);
          return aVal - bVal;
        case "price-desc":
          aVal = parseInt(a.getAttribute("data-price") || "0", 10);
          bVal = parseInt(b.getAttribute("data-price") || "0", 10);
          return bVal - aVal;
        default:
          return 0;
      }
    });

    // 描画
    elements.grid.innerHTML = "";
    cards.forEach(function(card) {
      elements.grid.appendChild(card.cloneNode(true));
    });

    // 削除ハンドラ再登録
    attachDeleteHandlers();

    // カウント更新
    updatePageCount(cards.length);

    // フィルタ結果が0件の場合
    if (cards.length === 0 && Object.keys(loadedCards).length > 0) {
      elements.grid.innerHTML = '<div class="omni-wishlist-page__no-results" style="grid-column: 1 / -1; text-align: center; padding: 3rem;"><p>条件に一致する商品がありません。フィルタを変更してください。</p></div>';
    }
  }

  function attachDeleteHandlers() {
    var deleteButtons = safeQSA("[data-wishlist-delete]", elements.grid);
    deleteButtons.forEach(function(btn) {
      // 既存のリスナーを避けるためcloneNode
      btn.onclick = handleDelete;
    });
  }

  function handleDelete(e) {
    e.preventDefault();
    e.stopPropagation();

    var card = e.target.closest("[data-wishlist-card]");
    if (!card) return;

    var handle = card.getAttribute("data-handle");
    var productGid = card.getAttribute("data-product-gid");

    // Optimistic UI
    card.style.opacity = "0.5";
    card.style.pointerEvents = "none";

    var promise;
    if (isLoggedIn() || productGid) {
      promise = removeFromServer(productGid);
    } else {
      promise = Promise.resolve(true);
    }

    promise.then(function(success) {
      if (!isLoggedIn()) {
        removeLocalWishlistItem(handle);
        success = true;
      }

      if (success) {
        wishlistItems = wishlistItems.filter(function(item) { return item.handle !== handle; });
        delete loadedCards[handle];
        card.remove();
        var newCount = Object.keys(loadedCards).length;
        updatePageCount(newCount);
        if (newCount === 0) {
          showEmpty(true);
          loadEmptyStateContent();
        }
      } else {
        card.style.opacity = "1";
        card.style.pointerEvents = "auto";
      }
    });
  }

  // ===== 空状態コンテンツ =====
  
  /**
   * LocalStorage から最近見た商品を取得
   * TOPページと同じ形式（omni_rv_v1）
   * @returns {Array<string>} handle の配列
   */
  function getRecentlyViewed() {
    var KEY = "omni_rv_v1";
    try {
      var raw = localStorage.getItem(KEY);
      if (!raw) {
        console.log("[wishlist] getRecentlyViewed: localStorage empty");
        return [];
      }
      
      var data = JSON.parse(raw);
      
      // 形式: { items: [{handle: "..."}, ...], last: productId }
      if (data && data.items && Array.isArray(data.items)) {
        var handles = data.items
          .map(function(item) { 
            return item && item.handle; 
          })
          .filter(function(h) { 
            return h && typeof h === "string"; 
          });
        
        console.log("[wishlist] getRecentlyViewed: found " + handles.length + " items", handles);
        return handles;
      }
      
      // フォールバック: 配列形式
      if (Array.isArray(data)) {
        var handles = data
          .map(function(item) { 
            return (item && item.handle) || item; 
          })
          .filter(function(h) { 
            return h && typeof h === "string"; 
          });
        
        console.log("[wishlist] getRecentlyViewed: found " + handles.length + " items (fallback)", handles);
        return handles;
      }
      
      console.warn("[wishlist] getRecentlyViewed: unexpected format", data);
      return [];
      
    } catch (e) {
      console.error("[wishlist] getRecentlyViewed: error", e);
      return [];
    }
  }
  
  /**
   * Recently Viewed の要素を毎回取得（elements.* を信用しない）
   */
  function getRecentlyViewedEls() {
    return {
      section: document.querySelector("[data-wishlist-recently-viewed]"),
      grid: document.querySelector("[data-wishlist-recently-viewed-grid]")
    };
  }

  /**
   * Recommendations の要素を毎回取得
   */
  function getRecommendationsEls() {
    return {
      section: document.querySelector("[data-wishlist-recommendations]"),
      grid: document.querySelector("[data-wishlist-recommendations-grid]")
    };
  }

  /**
   * 最近見たで描画済みの handle を取得（重複除外用）
   * @returns {Array<string>} handle の配列
   */
  function getRenderedRecentlyViewedHandles() {
    var els = getRecentlyViewedEls();
    var grid = els && els.grid;
    if (!grid) return [];
    
    return Array.from(grid.querySelectorAll(".omni-wishlist-card-wrapper[data-handle]"))
      .map(function(el) { 
        return el.getAttribute("data-handle"); 
      })
      .filter(Boolean);
  }

  /**
   * 空状態で最近見た・おすすめを表示
   * Wishlist が 0件 のときに必ず呼ぶ
   * 
   * ★ 重要：最近見た → おすすめ の順で直列実行し、重複を排除
   */
  function loadEmptyStateContent() {
    console.log("[wishlist] loadEmptyStateContent: loading recently viewed and recommendations");
    
    // requestAnimationFrame で DOM差し替え後も確実に実行
    return new Promise(function(resolve) {
      requestAnimationFrame(function() {
        // ★ 直列実行：最近見た → おすすめ（重複排除）
        loadRecentlyViewed()
          .then(function() {
            // 最近見たで描画された handle を収集
            var rvHandles = getRenderedRecentlyViewedHandles();
            var excludeSet = new Set(rvHandles);
            console.log("[wishlist] excludeSet for recommendations:", Array.from(excludeSet));
            
            // おすすめ商品を表示（最近見たと被るものは除外）
            return loadRecommendations(excludeSet);
          })
          .then(function() {
            console.log("[wishlist] loadEmptyStateContent: completed");
            resolve();
          })
          .catch(function(err) {
            console.warn("[wishlist] loadEmptyStateContent: error", err);
            resolve();
          });
      });
    });
  }

  /**
   * 最近見た商品を表示（空状態で使用）
   * hidden 属性で制御（inline style は使わない）
   */
  function loadRecentlyViewed() {
    // ★毎回 live DOM を取り直す（elements.* を信用しない）
    var els = getRecentlyViewedEls();
    var section = els.section;
    var grid = els.grid;
    
    if (!grid || !section) {
      console.warn("[wishlist] recently viewed section/grid not found", { 
        section: !!section, 
        grid: !!grid 
      });
      return Promise.resolve();
    }

    // ★ handle をユニーク化してから上限4件に絞る
    var recentlyViewedRaw = getRecentlyViewed();
    var seen = {};
    var recentlyViewed = [];
    
    for (var i = 0; i < recentlyViewedRaw.length; i++) {
      var h = recentlyViewedRaw[i];
      if (!h || seen[h]) continue; // 空またはすでに見たhandleはスキップ
      seen[h] = true;
      recentlyViewed.push(h);
      if (recentlyViewed.length >= 4) break; // 4件に達したら終了
    }
    
    console.log("[wishlist] loadRecentlyViewed: items=" + recentlyViewed.length + " (unique)");

    if (recentlyViewed.length === 0) {
      section.hidden = true;
      return Promise.resolve();
    }

    // ★表示解除は hidden で統一
    section.hidden = false;
    section.style.display = ""; // 補助的にクリア

    grid.innerHTML = "";
    var loadedCount = 0;

    var promises = recentlyViewed.map(function(handle) {
      return fetchProductCard(handle).then(function(html) {
        if (html) {
          var wrap = document.createElement("div");
          wrap.innerHTML = html;
          var card = wrap.querySelector(".omni-wishlist-card-wrapper");
          if (card) {
            var deleteBtn = safeQS("[data-wishlist-remove]", card);
            if (deleteBtn) deleteBtn.remove();
            grid.appendChild(card);
            loadedCount++;
          } else {
            console.warn("[wishlist] recently viewed card not found", handle);
          }
        }
      }).catch(function(err) {
        console.warn("[wishlist] recently viewed fetch error", handle, err);
      });
    });

    return Promise.all(promises).then(function() {
      console.log("[wishlist] loadRecentlyViewed: loaded " + loadedCount + " cards");
      // ★ hidden で統一（style.display への差し戻しをやめる）
      section.hidden = !(loadedCount > 0);
    });
  }

  /**
   * おすすめ商品を表示（空状態で使用）
   * hidden 属性で制御
   * 
   * @param {Set<string>} excludeSet - 除外するhandle（最近見たと重複排除）
   */
  function loadRecommendations(excludeSet) {
    excludeSet = excludeSet || new Set();
    
    // ★毎回 live DOM を取り直す
    var els = getRecommendationsEls();
    var section = els.section;
    var grid = els.grid;
    
    if (!grid || !section) {
      console.warn("[wishlist] recommendations section/grid not found", { 
        section: !!section, 
        grid: !!grid 
      });
      return Promise.resolve();
    }

    var recentlyViewed = getRecentlyViewed();
    var seedHandle = recentlyViewed[0];
    console.log("[wishlist] loadRecommendations: seedHandle=" + seedHandle);

    if (!seedHandle) {
      section.hidden = true;
      return Promise.resolve();
    }

    // ★表示解除は hidden で統一
    section.hidden = false;
    section.style.display = ""; // 補助的にクリア

    return fetch("/products/" + encodeURIComponent(seedHandle) + ".json")
    .then(function(res) {
      if (!res.ok) throw new Error("Product not found");
      return res.json();
    })
    .then(function(productData) {
      var productId = productData.product.id;
      // ★ limit=12 で多めに取得（除外後に4件残すため）
      return fetch("/recommendations/products.json?product_id=" + productId + "&limit=12");
    })
    .then(function(res) {
      if (!res.ok) throw new Error("Recommendations not found");
      return res.json();
    })
    .then(function(recData) {
      if (!recData.products || recData.products.length === 0) {
        section.hidden = true;
        return;
      }

      // ★ handleでユニーク化しつつ除外して先頭4件に絞る
      var picked = [];
      var used = {};
      
      for (var i = 0; i < recData.products.length; i++) {
        var p = recData.products[i];
        var h = p && p.handle;
        
        if (!h) continue; // handleが無い場合はスキップ
        if (excludeSet.has(h)) continue; // 最近見たと被るものを除外
        if (used[h]) continue; // おすすめ内の重複も除外
        
        used[h] = true;
        picked.push(p);
        
        if (picked.length >= 4) break; // 4件に達したら終了
      }
      
      console.log("[wishlist] loadRecommendations: picked " + picked.length + " products (after exclusion)");

      if (picked.length === 0) {
        section.hidden = true;
        return;
      }

      grid.innerHTML = "";
      var loadedCount = 0;

      var promises = picked.map(function(product) {
        return fetchProductCard(product.handle).then(function(html) {
          if (html) {
            var wrap = document.createElement("div");
            wrap.innerHTML = html;
            var card = wrap.querySelector(".omni-wishlist-card-wrapper");
            if (card) {
              var deleteBtn = safeQS("[data-wishlist-remove]", card);
              if (deleteBtn) deleteBtn.remove();
              grid.appendChild(card);
              loadedCount++;
            } else {
              console.warn("[wishlist] recommended card not found", product.handle);
            }
          }
        });
      });

      return Promise.all(promises).then(function() {
        console.log("[wishlist] loadRecommendations: loaded " + loadedCount + " cards");
        // ★ hidden で統一（style.display への差し戻しをやめる）
        section.hidden = !(loadedCount > 0);
      });
    })
    .catch(function(err) {
      console.warn("[wishlist] loadRecommendations error", err);
      section.hidden = true;
    });
  }

  // ===== フィルタハンドラ =====
  function attachFilterHandlers() {
    if (elements.filterCategory) {
      elements.filterCategory.addEventListener("change", applyFilterAndSort);
    }
    if (elements.filterRank) {
      elements.filterRank.addEventListener("change", applyFilterAndSort);
    }
    if (elements.filterInStock) {
      elements.filterInStock.addEventListener("change", applyFilterAndSort);
    }
    if (elements.sortSelect) {
      elements.sortSelect.addEventListener("change", applyFilterAndSort);
    }
  }

  // ===== メイン描画 =====
  function renderWishlistItems() {
    if (!elements.grid) {
      console.warn("[wishlist] grid element not found");
      return Promise.resolve();
    }

    showLoading(true);
    showEmpty(false);

    elements.grid.innerHTML = "";
    loadedCards = {};
    categories = {};
    ranks = {};

    if (wishlistItems.length === 0) {
      showLoading(false);
      showEmpty(true);
      updatePageCount(0);
      loadEmptyStateContent();
      return Promise.resolve();
    }

    var promises = wishlistItems.map(function(item) {
      return fetchProductCard(item.handle).then(function(html) {
        if (html) {
          var card = renderCard(html, item);
          if (card) {
            loadedCards[item.handle] = card;
            var category = card.getAttribute("data-category");
            if (category) categories[category] = true;
            var rank = card.getAttribute("data-rank");
            if (rank) ranks[rank] = true;
          } else {
            console.warn("[wishlist] card render failed for handle", item.handle);
          }
        } else {
          console.warn("[wishlist] card fetch failed for handle", item.handle);
        }
      }).catch(function(err) {
        console.warn("[wishlist] card error for handle", item.handle, err);
      });
    });

    return Promise.all(promises).then(function() {
      var loadedCount = Object.keys(loadedCards).length;
      console.log("[wishlist] loaded cards", loadedCount, "of", wishlistItems.length);
      
      // ★取得失敗時のエラー表示
      if (wishlistItems.length > 0 && loadedCount === 0) {
        var errorEl = document.querySelector("[data-wishlist-errors]");
        if (errorEl) {
          errorEl.hidden = false;
          errorEl.textContent = "商品情報の取得に失敗しました。再読み込みをお試しください。";
        }
        console.error("[wishlist] all cards failed to load");
      }
      
      populateCategoryFilter();
      populateRankFilter();
      applyFilterAndSort();
      showLoading(false);
    });
  }

  // ===== 初期化 =====
  function initWishlistPage() {
    console.log("[wishlist] initWishlistPage started");

    cacheElements();

    if (!elements.grid) {
      console.warn("[wishlist] Required element [data-wishlist-grid] not found");
      return;
    }

    attachFilterHandlers();

    // データ取得: getHandles()を使用（ゲストはLocalStorage、ログインはサーバー）
    getHandles().then(function(handles) {
      console.log("[wishlist] initWishlistPage: handles.length=" + handles.length);
      
      // handle配列をwishlistItems形式に変換
      wishlistItems = handles.map(function(handle, index) {
        return {
          handle: handle,
          productGid: null,
          addedAt: Date.now() - index * 1000 // 順序保持用
        };
      });

      return renderWishlistItems();
    }).then(function() {
      // ×ボタンのイベントデリゲーション
      attachRemoveHandlers();
      console.log("[wishlist] initWishlistPage completed");
    }).catch(function(err) {
      console.error("[wishlist] initWishlistPage error", err);
      showLoading(false);
      showEmpty(true);
      // エラー時も最近見た・おすすめを表示
      loadEmptyStateContent();
    });
  }

  // ===== お気に入り解除（×ボタン）処理 =====
  var removingHandles = new Set(); // 連打防止

  function attachRemoveHandlers() {
    if (!elements.grid) return;

    // イベントデリゲーション
    elements.grid.addEventListener("click", function(e) {
      // ★リンク（画像・商品名）はそのまま遷移させる（preventDefault しない）
      if (e.target.closest("a[href]")) {
        return; // リンクのデフォルト動作を許可
      }

      // ★カートフォーム内クリックは通常通り（submitは許可）
      if (e.target.closest(".omni-wishlist-add-to-cart-form")) {
        return;
      }

      // ×ボタンのみ preventDefault
      var btn = e.target.closest("[data-wishlist-remove]");
      if (!btn) return;

      e.preventDefault();
      e.stopPropagation();

      // 連打防止（data-busy フラグ）
      if (btn.dataset.busy === "1") return;
      btn.dataset.busy = "1";

      var card = btn.closest("[data-wishlist-card]");
      if (!card) {
        btn.dataset.busy = "0";
        return;
      }

      var handle = card.getAttribute("data-handle");
      var productGid = card.getAttribute("data-product-gid");

      if (!handle || removingHandles.has(handle)) {
        btn.dataset.busy = "0";
        return;
      }

      // 削除処理を try/catch で包む
      try {
        removeFromWishlist(handle, productGid, card);
      } catch (err) {
        console.error("[wishlist] removeFromWishlist error", err);
        removingHandles.delete(handle);
      } finally {
        // Optimistic UIなので busy 解除は短く（100ms）
        setTimeout(function() {
          btn.dataset.busy = "0";
        }, 100);
      }
    });
  }

  /**
   * お気に入りから削除（完全Optimistic UI版）
   * 
   * ★ 設計方針：
   * - クリック直後に即座にUIを更新（体感150〜200ms）
   * - API呼び出しはバックグラウンドで実行
   * - 失敗してもロールバックしない（LocalStorageは削除済み）
   */
  function removeFromWishlist(handle, productGid, cardElement) {
    if (removingHandles.has(handle)) return;
    removingHandles.add(handle);

    console.log("[wishlist] removeFromWishlist: start (optimistic)", handle);

    // ===== STEP 1: 即座にUIを更新（Optimistic） =====
    
    // 1-1. カードにアニメーションクラスを付与
    cardElement.classList.add("is-removing");
    
    // 1-2. 180ms後にDOMから削除
    setTimeout(function() {
      try {
        cardElement.remove();
      } catch (e) {
        console.warn("[wishlist] card remove failed", e);
      }
    }, 180);

    // 1-3. メモリ上のデータを即時更新
    wishlistItems = wishlistItems.filter(function(it) {
      return it.handle !== handle;
    });
    
    // loadedCardsからも削除
    if (typeof loadedCards !== "undefined" && loadedCards[handle]) {
      delete loadedCards[handle];
    }

    var newCount = wishlistItems.length;

    // 1-4. ページ内の件数を即時更新
    updatePageCount(newCount);

    // 1-5. ヘッダーバッジを即時更新
    if (typeof window.updateWishlistBadges === "function") {
      window.updateWishlistBadges(newCount);
    }

    // 1-6. イベント通知（即時）
    try {
      dispatchCountChangeEvent(newCount, {
        reason: "remove",
        handle: handle,
        productGid: productGid
      });
    } catch (e) {
      console.warn("[wishlist] dispatchCountChangeEvent failed", e);
    }

    // 1-7. 0件になったら空状態を即時表示
    if (newCount === 0) {
      showEmpty(true);
      loadEmptyStateContent();
    }

    console.log("[wishlist] UI updated (optimistic), newCount:", newCount);

    // ===== STEP 2: LocalStorageから削除（ゲストの場合） =====
    if (!isLoggedIn() && window.OMNI && window.OMNI.wishlist) {
      try {
        var items = window.OMNI.wishlist.getLocalWishlistItems();
        items = items.filter(function(it) {
          return it.handle !== handle && it.productGid !== productGid;
        });
        window.OMNI.wishlist.saveLocalWishlistItems(items);
        console.log("[wishlist] removed from localStorage:", handle);
      } catch (e) {
        console.warn("[wishlist] localStorage remove failed", e);
      }
    }

    // ===== STEP 3: APIに解除リクエスト（バックグラウンド） =====
    if (!productGid) {
      productGid = "gid://shopify/Product/" + handle; // フォールバック
    }

    var anonId = getAnonId();
    
    // ★ keepalive: true で遷移時もリクエストを完了させる
    fetch("/apps/wishlist/toggle", {
      method: "POST",
      keepalive: true,
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      body: JSON.stringify({
        product_gid: productGid,
        anon_id: anonId
      })
    })
    .then(function(res) { return res.json(); })
    .then(function(data) {
      removingHandles.delete(handle);
      
      if (!data || !data.ok) {
        console.warn("[wishlist] remove API failed (non-critical)", data);
        // UIはすでに更新済み、ロールバックしない
        return;
      }

      // 後追い整合：サーバーのcountで更新（ドリフト解消）
      if (typeof data.count === "number") {
        updatePageCount(data.count);
        if (typeof window.updateWishlistBadges === "function") {
          window.updateWishlistBadges(data.count);
        }
      }

      console.log("[wishlist] removed successfully (API confirmed):", handle);
    })
    .catch(function(err) {
      console.warn("[wishlist] remove API error (non-critical)", err);
      removingHandles.delete(handle);
      // UIはすでに更新済み、ロールバックしない
    });
  }

  // ===== 商品ページ用ボタン初期化（最低限） =====
  function initProductButtons() {
    // 既存のomni-wishlist.jsがあるので、ここでは最低限のフォールバックのみ
    console.log("[wishlist] initProductButtons - delegating to existing implementation");
  }

  // ===== グローバル公開 =====
  window.OMNI.wishlist.initWishlistPage = initWishlistPage;
  window.OMNI.wishlist.initProductButtons = initProductButtons;
  window.OMNI.wishlist.safeQS = safeQS;
  window.OMNI.wishlist.safeQSA = safeQSA;
  window.OMNI.wishlist.escapeSelector = escapeSelector;
  
  // ★必須: データ取得関数
  window.OMNI.wishlist.getHandles = getHandles;
  window.OMNI.wishlist.getCount = getCount;
  window.OMNI.wishlist.getRecentlyViewed = getRecentlyViewed;
  
  // LocalStorage操作（toggle処理から使用）
  window.OMNI.wishlist.getLocalWishlistItems = getLocalWishlistItems;
  window.OMNI.wishlist.saveLocalWishlistItems = saveLocalWishlistItems;
  window.OMNI.wishlist.cleanupLocalWishlistItems = cleanupLocalWishlistItems;
  
  // デバッグヘルパー（マージ形式で上書きしない）
  Object.assign(window.OMNI.wishlist.debug, {
    getLocalItems: getLocalWishlistItems,
    saveLocalItems: saveLocalWishlistItems,
    clearLocal: function() {
      localStorage.removeItem(WISHLIST_ITEMS_KEY);
      console.log("[wishlist] debug: cleared localStorage");
    },
    showLocal: function() {
      var items = getLocalWishlistItems();
      console.log("[wishlist] debug: localStorage items", items);
      return items;
    },
    testAdd: function(handle) {
      var items = getLocalWishlistItems();
      items.push({ handle: handle, productGid: null, addedAt: Date.now() });
      saveLocalWishlistItems(items);
      console.log("[wishlist] debug: added " + handle);
    },
    // キー名を確認用
    getKey: function() {
      return WISHLIST_ITEMS_KEY;
    },
    // 生データを確認
    getRaw: function() {
      var raw = localStorage.getItem(WISHLIST_ITEMS_KEY);
      console.log("[wishlist] debug: raw localStorage", raw);
      return raw;
    }
  });

  // ===== 自動初期化 =====
  document.addEventListener("DOMContentLoaded", function() {
    // wishlistページの判定
    if (safeQS("[data-omni-page=\"wishlist\"]")) {
      initWishlistPage();
    }
  });

})();

